import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Button,
} from '@mui/material';
import EditTimeModal from './EditTimeModal';
import { getAccessToken } from '../../utils/auth';

const TimeEntriesAdmin = () => {
  const [selectedUser, setSelectedUser] = useState('');
  const [users, setUsers] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [error, setError] = useState('');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [deletingId, setDeletingId] = useState(null); // <-- JS, ingen typer

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const token = await getAccessToken();
      const response = await fetch('https://app.recyconelement.dk/api/user/users.php', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + token,
        },
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setUsers(result.data);
      } else {
        setError(result.message || 'Fejl ved hentning af brugere');
      }
    } catch (error) {
      console.error('Fejl ved hentning af brugere:', error);
      setError('Der opstod en fejl. Prøv igen senere.');
    }
  };

  const handleUserChange = async (event) => {
    const userId = event.target.value;
    setSelectedUser(userId);

    try {
      const token = await getAccessToken();
      const response = await fetch(`https://app.recyconelement.dk/api/time/get_all_time_entries.php?user_id=${userId}&days=30`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + token,
        },
      });

      const data = await response.json();

      if (response.ok) {
        setTimeEntries(data.data);
      } else {
        console.error('Error fetching time entries:', data.message || 'Unknown error');
      }
    } catch (error) {
      console.error('Error fetching time entries:', error);
    }
  };

  const openEditModal = (entry) => {
    setEditEntry(entry);
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditEntry(null);
	
  };

  const handleSaveEdit = (updatedEntry) => {
    setTimeEntries((prevEntries) =>
      prevEntries.map((entry) => (entry.id === updatedEntry.id ? updatedEntry : entry))
    );
	handleUserChange({ target: { value: selectedUser } });
  };

   const handleDelete = async (entryId) => {
    const ok = window.confirm('Er du sikker på, at du vil slette denne registrering?');
    if (!ok) return;

    setDeletingId(entryId);
    setError('');

    try {
      const token = await getAccessToken();

      const ENDPOINT_URL = 'https://app.recyconelement.dk/api/time/delete_time_entry.php';

      const response = await fetch(ENDPOINT_URL, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + token,
        },
        body: JSON.stringify({ id: entryId }),
      });

      const result = await response.json();

      if (!response.ok || result?.success === false) {
        throw new Error(result?.message || 'Kunne ikke slette registreringen');
      }

      // Optimistisk UI-opdatering
      setTimeEntries((prev) => prev.filter((e) => e.id !== entryId));
      // Eller: await handleUserChange({ target: { value: selectedUser } });
    } catch (err) {
      console.error('Fejl ved sletning:', err);
      setError(err?.message || 'Der opstod en fejl ved sletning.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Container>
      <Box>
        <Typography variant="h4">Time Entries Administration</Typography>
        <FormControl fullWidth margin="normal">
          <InputLabel id="user-select-label">Select User</InputLabel>
          <Select
            labelId="user-select-label"
            value={selectedUser}
            onChange={handleUserChange}
          >
            {users.map((user) => (
              <MenuItem key={user.id} value={user.id}>
                {user.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}
        {timeEntries.length > 0 && (
          <TableContainer component={Paper} style={{ marginTop: '20px' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Dato</TableCell>
                  <TableCell>Start tid</TableCell>
                  <TableCell>Slut tid</TableCell>
                  <TableCell>Frokost</TableCell>
                  <TableCell>Handler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {timeEntries.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell>{entry.date}</TableCell>
                    <TableCell>{entry.start_time}</TableCell>
                    <TableCell>{entry.end_time}</TableCell>
                    <TableCell>{entry.break}</TableCell>
                    <TableCell>
                      <Button onClick={() => openEditModal(entry)}>Rediger</Button>
                      <Button
                        onClick={() => handleDelete(entry.id)}
                        disabled={deletingId === entry.id}
                      >
                        {deletingId === entry.id ? 'Sletter…' : 'Slet'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
      {editModalOpen && (
        <EditTimeModal
          open={editModalOpen}
          onClose={closeEditModal}
          onSave={handleSaveEdit}
          initialData={editEntry}
        />
      )}
    </Container>
  );
};

export default TimeEntriesAdmin;
